import { computed, makeObservable } from 'mobx'
import { orderBy } from 'lodash'
import ModalViewModel from 'infra/ModalViewModel'
import {
  JobProgression,
  JobProgressionFlow,
  JobProgressionStepStatus,
} from 'jobs/progression/JobProgression'
import Member from 'iam/models/Member'
import { Id } from 'types/definitions'
import { formatToLongDateWithComma } from 'utils/date-util'
import { NOTE_AUTHOR_ROLE_LABELS } from '../../JobNotesCard/JobNoteItemViewModel'

export interface JobProgressionHistoryActor {
  member: Member
  roleLabel?: string
}

export interface JobProgressionHistoryItem {
  id: Id
  title: string
  formattedDateCreated: string | null
  createdBy: JobProgressionHistoryActor | null
}

/**
 * Dialog listing every progression step for the job as a timeline.
 */
export default class JobProgressionHistoryViewModel extends ModalViewModel {
  constructor(
    private readonly getProgression: () => JobProgression | undefined,
    private readonly getStepTitle: (
      flow: JobProgressionFlow,
      status: JobProgressionStepStatus,
    ) => string,
    private readonly resolveMember: (userPublicId: string) => Member,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get items(): JobProgressionHistoryItem[] {
    const progression = this.getProgression()
    if (!progression) {
      return []
    }

    const flow = progression.progressionFlow
    return orderBy(progression.steps, ['dateCreated'], ['desc']).map(
      (step) => ({
        id: step.id,
        title: this.getStepTitle(flow, step.status),
        formattedDateCreated: step.dateCreated
          ? formatToLongDateWithComma(step.dateCreated)
          : null,
        createdBy: this.toActor(step.createdByUserPublicId),
      }),
    )
  }

  private toActor(userPublicId?: string): JobProgressionHistoryActor | null {
    if (!userPublicId) {
      return null
    }

    const member = this.resolveMember(userPublicId)
    return {
      member,
      roleLabel: member.roles
        .map((r) => NOTE_AUTHOR_ROLE_LABELS[r.name.trim()])
        .find(Boolean),
    }
  }
}
